// Custom packages
import {
  isPublicQrInlineName,
  isPublicQrInlineType,
} from "src/app/modules/public-qr-v3/public-qr-v3.utils";

/*
 * THE NAME OF A FILE AS A LINK, in the download dialog of the 3.0.0 archive («Scarica i documenti»):
 * which files open in a new tab, and when the memory of a file shown there is given back.
 *
 * The file travels the road of «Scarica»: the authenticated route, asked for the file to show
 * (`disposition=inline`), and the tab shows it from a Blob. Not a link to its public address under
 * `/uploads`: that one opens without a session, for whoever holds it, and it is the road the
 * back-end means to close (signed links, SEC-02).
 *
 * The back-end shows PDF, PNG and JPEG only, deciding on the type stored with the file. That closed
 * list is read from the helpers of the public QR page, which opens its files the same way: the
 * front-end keeps ONE copy of it.
 */

/** How often a tab showing a file is checked, to give the file's memory back once it is closed */
export const ARCHIVE_FILE_TAB_POLL_MS = 5000;

/**
 * One file of the dialog, the members read here: an entry of `data.document[]`.
 *
 * @since 3.0.0
 */
export interface ArchiveOpenableFile {
  /** The type stored with the file at upload; missing on some records */
  mimetype?: unknown;
  /** The name the file was uploaded with */
  originalName?: unknown;
}

/**
 * Whether clicking the name of a file opens a tab for it.
 *
 * Decided BEFORE asking for the file, because the tab has to be opened inside the click: one opened
 * once the answer is back is a pop-up to the browser, and gets blocked. The stored type decides when
 * the record has one, because it is what the back-end decides on; without one, the name does. Still a
 * guess: the type of the answer has the last word (@see archiveFileShowsInTab).
 *
 * @since 3.0.0
 *
 * @param file An entry of `data.document[]`
 */
export function archiveFileOpensInTab(file: ArchiveOpenableFile | null | undefined): boolean {
  if (!file) {
    return false;
  }
  if (typeof file.mimetype === "string" && file.mimetype.trim() !== "") {
    return isPublicQrInlineType(file.mimetype);
  }
  return isPublicQrInlineName(file.originalName);
}

/**
 * Whether a file read from the back-end may be shown in the tab, by the type of the answer.
 *
 * ⚠️ This is the security boundary of the link. The tab shows the Blob from Archedi's own address,
 * so a type outside the closed list — an uploaded HTML or SVG — would run its scripts inside Archedi,
 * with the session of the user. Whatever is not on the list is saved, never shown.
 *
 * @since 3.0.0
 *
 * @param blob The body of the answer
 */
export function archiveFileShowsInTab(blob: Blob | null | undefined): boolean {
  return !!blob && isPublicQrInlineType(blob.type);
}

/**
 * Give back the memory of a file shown in a tab once that tab is closed.
 *
 * The address of the file is kept while the tab is open, because the tab reads it again on a reload
 * and the viewer of the browser reads it again to save the file. Closing or reloading Archedi releases
 * every such address, and stops the check with it.
 *
 * @since 3.0.0
 *
 * @param tab    The tab showing the file
 * @param url    The object URL the tab shows
 * @param pollMs How often the tab is checked
 */
export function revokeArchiveFileUrlOnTabClose(
  tab: Window,
  url: string,
  pollMs: number = ARCHIVE_FILE_TAB_POLL_MS
): void {
  const timer = window.setInterval(() => {
    if (tab.closed) {
      window.clearInterval(timer);
      URL.revokeObjectURL(url);
    }
  }, pollMs);
}
